import React, { useState } from "react";
import { Box, Menu, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { CustomerCompany } from "../models/models";
import Location from "../../../shared/components/Location";
import useTranslation from "../../../shared/hooks/useTranslation";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import { CompanyGroup } from "../../companyGroups/models/models";
import DefaultCompanyLogo from "./DefaultCompanyLogo";
import { RelationshipTierCode } from "../../partnerships/models/models";
import { dt } from "../../../shared/themes/redesignTokens";

// =====================================================================
//  CARD TOKENS — match RedesignedMachineCard exactly
// =====================================================================

const card = {
  bg: dt.cardBg,
  border: `1px solid ${dt.cardBorder}`,
  radius: dt.radius,
  shadow: dt.cardShadow,
  shadowHover: dt.cardShadowHover,
  imageSizeLg: 80,
  imageSizeSm: 80,
};

// =====================================================================
//  TEXT ALIASES (same as machinery dt.text)
// =====================================================================

const text = {
  primary: dt.textPrimary,
  secondary: dt.textSecondary,
  tertiary: dt.textMuted,
};

// Emerald green for distance pill
const emerald = {
  color: "#10B981",
  bg: "rgba(16, 185, 129, 0.08)",
  border: "rgba(16, 185, 129, 0.18)",
};

// =====================================================================
//  COMPONENT
// =====================================================================

interface PartnershipStatusInfo {
  type: "partner" | "pending" | "none";
  tier?: RelationshipTierCode;
}

interface CompanyCardProps {
  company: CustomerCompany;
  index?: number;
  isOwnCompany?: boolean;
  isBookmarked?: boolean;
  onBookmarkToggle?: (companyId: string) => void;
  groups?: CompanyGroup[];
  onToggleGroup?: (companyId: string, groupId: string, isInGroup: boolean) => void;
  onCreateGroupWithCompany?: (name: string, companyId: string) => void;
  partnershipTier?: RelationshipTierCode;
  partnershipStatus?: PartnershipStatusInfo;
  onInvitePartner?: (company: CustomerCompany) => void;
  machineCount?: number;
}

const CompanyCard = ({
  company, index = 0, isOwnCompany = false, isBookmarked = false, onBookmarkToggle,
  groups = [], onToggleGroup, onCreateGroupWithCompany, partnershipTier, partnershipStatus,
  onInvitePartner, machineCount,
}: CompanyCardProps) => {
  const navigate = useNavigate();
  const { translate } = useTranslation(["partnerHub", "partnerships"]);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [showNewInput, setShowNewInput] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const menuOpen = Boolean(menuAnchor);
  const isPhone = useMediaQuery("(max-width:768px)");

  // customerType-based role detection (CRITICAL: uses customerType, NOT roleCode)
  const customerType = company.customerType || 0;
  const isServiceProvider = customerType === 2 || customerType === 3;
  const isMachineOwner = customerType === 1 || customerType === 3;
  const isHybrid = customerType === 3;

  const isCompanyInGroup = (group: CompanyGroup): boolean => {
    const cId = company.id?.toLowerCase();
    return group.items?.some((item) => item.customer?.id?.toLowerCase() === cId) || false;
  };
  const isInAnyGroup = groups.some(isCompanyInGroup);

  const handleClick = () => { if (menuOpen) return; navigate(`/companies/${company.id}`); };

  const handleBookmarkClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (onToggleGroup && groups.length > 0) { setMenuAnchor(e.currentTarget); }
    else if (onBookmarkToggle) { onBookmarkToggle(company.id); }
  };
  const handleMenuClose = () => { setMenuAnchor(null); setShowNewInput(false); setNewGroupName(""); };
  const handleToggle = (group: CompanyGroup) => { onToggleGroup?.(company.id, group.id, isCompanyInGroup(group)); };
  const handleCreateGroup = () => {
    if (newGroupName.trim() && onCreateGroupWithCompany) {
      onCreateGroupWithCompany(newGroupName.trim(), company.id);
      setNewGroupName(""); setShowNewInput(false);
    }
  };

  // Animation stagger 60ms, capped at 0.6s
  const animDelay = Math.min(index * 0.06, 0.6);

  // Logo renderer
  const renderLogo = (size: number) => {
    if (company?.logo?.content) {
      return (
        <Box sx={{
          width: size, height: size, borderRadius: "10px", overflow: "hidden",
          background: dt.rowBorder, display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <img
            src={company.logo.content}
            alt={company.name}
            style={{ width: size, height: size, objectFit: "contain", borderRadius: "10px" }}
          />
        </Box>
      );
    }
    return (
      <Box sx={{
        width: size, height: size, borderRadius: "10px", overflow: "hidden",
        background: dt.rowBorder, display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <DefaultCompanyLogo seed={company.id || company.name} size={size} />
      </Box>
    );
  };

  // Machine count label (singular/plural)
  const machineCountLabel = translate("partnerHub:card.machineCountLabel", { count: machineCount ?? 0 });

  // Full role labels for Tier 2
  const renderFullRoleLabels = () => {
    const labels: React.ReactNode[] = [];
    if (isHybrid || isServiceProvider) {
      labels.push(
        <Box key="sp" component="span" sx={{ color: dt.roleGreen, fontWeight: 600 }}>
          {translate("partnerHub:card.serviceProvider")}
        </Box>
      );
    }
    if (isHybrid || isMachineOwner) {
      labels.push(
        <Box key="mo" component="span" sx={{ color: dt.rolePurple, fontWeight: 600 }}>
          {translate("partnerHub:card.machineOwner")}
        </Box>
      );
    }
    if (labels.length === 0) return null;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12.5px", fontFamily: dt.font, flexWrap: "wrap" }}>
        {labels.map((label, i) => (
          <React.Fragment key={i}>
            {i > 0 && <Box component="span" sx={{ color: dt.textMuted, mx: "2px" }}>&middot;</Box>}
            {label}
          </React.Fragment>
        ))}
      </Box>
    );
  };

  // Whether Tier 2 has anything to show
  const hasRoleLabels = isServiceProvider || isMachineOwner;
  const hasMachineCount = machineCount != null && machineCount > 0;
  const hasTier2 = hasRoleLabels || hasMachineCount;

  // Partnership button base style (small pill)
  const partnerBtnBase = {
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
    padding: "2px 8px",
    borderRadius: "20px",
    fontSize: "10px",
    fontWeight: 600,
    fontFamily: dt.font,
    whiteSpace: "nowrap" as const,
    lineHeight: 1.4,
    letterSpacing: "0.02em",
    transition: "all .2s ease",
  };

  // Render partnership button based on status
  const renderPartnershipButton = () => {
    // Determine effective status — use partnershipStatus if provided, fall back to partnershipTier
    const effectiveType = partnershipStatus?.type
      ?? (partnershipTier ? "partner" : undefined);

    if (isOwnCompany || !effectiveType) return null;

    if (effectiveType === "partner") {
      return (
        <Box
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); navigate("/partnerships"); }}
          sx={{
            ...partnerBtnBase,
            backgroundColor: dt.blueSoft,
            color: dt.blue,
            border: `1px solid ${dt.blue}22`,
            cursor: "pointer",
            "&:hover": {
              backgroundColor: dt.blueGlow,
              borderColor: dt.blue,
            },
          }}
        >
          <HandshakeOutlinedIcon sx={{ fontSize: 12 }} />
          {translate("partnerships:partner.connectedButton")}
        </Box>
      );
    }

    if (effectiveType === "pending") {
      return (
        <Box
          sx={{
            ...partnerBtnBase,
            backgroundColor: dt.blueSoft,
            color: dt.blue,
            border: `1px solid ${dt.blue}22`,
            opacity: 0.6,
            cursor: "default",
          }}
        >
          <HandshakeOutlinedIcon sx={{ fontSize: 12 }} />
          {translate("partnerships:invitation.pending")}
        </Box>
      );
    }

    if (effectiveType === "none") {
      return (
        <Box
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onInvitePartner?.(company);
          }}
          sx={{
            ...partnerBtnBase,
            backgroundColor: "transparent",
            color: dt.blue,
            border: `1px solid ${dt.blue}40`,
            cursor: "pointer",
            "&:hover": {
              backgroundColor: dt.blueSoft,
              borderColor: dt.blue,
            },
          }}
        >
          <HandshakeOutlinedIcon sx={{ fontSize: 12 }} />
          {translate("partnerships:invite.buttonShort")}
        </Box>
      );
    }

    return null;
  };

  // Top-right corner: partnership button + bookmark icon
  const renderTopRight = () => {
    const showBookmark = !isOwnCompany && (onToggleGroup || onBookmarkToggle);
    const partnershipBtn = renderPartnershipButton();
    if (!showBookmark && !partnershipBtn) return null;
    return (
      <Box sx={{ position: "absolute", top: "10px", right: "10px", zIndex: 2, display: "flex", alignItems: "center", gap: "6px" }}>
        {partnershipBtn}
        {showBookmark && (
          <Box
            onClick={handleBookmarkClick}
            sx={{
              cursor: "pointer",
              transition: "all .2s",
              display: "flex", alignItems: "center",
              color: isInAnyGroup || isBookmarked ? dt.amber : text.tertiary,
              "&:hover": { color: isInAnyGroup || isBookmarked ? dt.amber : dt.blue },
            }}
          >
            {isInAnyGroup || isBookmarked
              ? <BookmarkIcon sx={{ fontSize: 22 }} />
              : <BookmarkBorderOutlinedIcon sx={{ fontSize: 22 }} />}
          </Box>
        )}
      </Box>
    );
  };

  // Save-to-group dropdown menu (kept for functionality)
  const renderGroupMenu = () => {
    if (!menuOpen) return null;
    return (
      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              width: 240, borderRadius: "14px",
              boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
              border: `1px solid ${dt.cardBorder}`,
              mt: 0.5, p: "6px",
            },
          },
        }}
      >
        <Box sx={{ padding: "4px 8px", fontSize: "11px", color: text.tertiary, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: dt.font }}>
          {translate("partnerHub:groups.saveToGroup")}
        </Box>
        <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
          {groups.map((group) => {
            const isIn = isCompanyInGroup(group);
            return (
              <Box key={group.id} sx={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 8px", borderRadius: dt.radiusSm, color: text.primary, fontSize: "13px", fontFamily: dt.font, "&:hover": { background: dt.inputBg } }}>
                <Box sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{group.name}</Box>
                <Box sx={{ fontSize: "11px", color: text.tertiary, mr: "4px" }}>{group.items?.length || 0}</Box>
                {isIn ? (
                  <Box onClick={() => handleToggle(group)} sx={{ fontSize: "11px", fontWeight: 600, padding: "2px 8px", borderRadius: "4px", cursor: "pointer", fontFamily: dt.font, color: "#EF4444", border: "1px solid #FECACA", background: "#FEF2F2", whiteSpace: "nowrap", "&:hover": { background: "#FEE2E2", borderColor: "#EF4444" } }}>
                    {translate("partnerHub:groups.remove")}
                  </Box>
                ) : (
                  <Box onClick={() => handleToggle(group)} sx={{ fontSize: "11px", fontWeight: 600, padding: "2px 8px", borderRadius: "4px", cursor: "pointer", fontFamily: dt.font, color: dt.blue, border: `1px solid ${dt.blue}40`, background: `${dt.blue}08`, whiteSpace: "nowrap", "&:hover": { background: `${dt.blue}18`, borderColor: dt.blue } }}>
                    {translate("partnerHub:groups.add")}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
        <Box sx={{ height: "1px", background: dt.cardBorder, my: "4px" }} />
        {!showNewInput ? (
          <Box onClick={() => setShowNewInput(true)} sx={{ display: "flex", alignItems: "center", gap: "6px", padding: "7px 8px", borderRadius: dt.radiusSm, color: dt.blue, fontSize: "13px", cursor: "pointer", fontFamily: dt.font, "&:hover": { background: dt.inputBg } }}>
            <AddOutlinedIcon sx={{ fontSize: 13 }} />{translate("partnerHub:groups.newGroup")}
          </Box>
        ) : (
          <Box sx={{ display: "flex", gap: "4px", padding: "4px 8px" }}>
            <input autoFocus value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleCreateGroup()} placeholder={translate("partnerHub:groups.newGroup")}
              style={{ flex: 1, border: `1px solid ${dt.cardBorder}`, borderRadius: "6px", padding: "5px 8px", fontSize: "12px", fontFamily: 'var(--font-sans)', color: text.primary, outline: "none", background: "#F8FAFC" }} />
            <Box onClick={handleCreateGroup} sx={{ width: 28, height: 28, borderRadius: "6px", background: dt.blue, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, fontSize: "16px", fontWeight: 700 }}>+</Box>
          </Box>
        )}
      </Menu>
    );
  };

  // =====================================================================
  //  PHONE LAYOUT
  // =====================================================================

  if (isPhone) {
    return (
      <Box
        onClick={handleClick}
        sx={{
          background: card.bg,
          border: card.border,
          borderRadius: dt.radiusSm,
          padding: "14px",
          boxShadow: card.shadow,
          position: "relative",
          width: "100%",
          boxSizing: "border-box",
          overflow: "hidden",
          fontFamily: dt.font,
          transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
          cursor: "pointer",
          "&:hover": { transform: "translateY(-2px)", boxShadow: card.shadowHover },
          animation: `cardReveal 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${animDelay}s both`,
        }}
      >
        {renderTopRight()}

        {/* Logo centered */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: "12px" }}>
          {renderLogo(card.imageSizeSm)}
        </Box>

        {/* Company name */}
        <Box sx={{
          textAlign: "center", mb: "6px",
        }}>
          <Box sx={{
            fontSize: "15px", fontWeight: 600, fontFamily: dt.font, color: text.primary,
            lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
            overflow: "hidden", wordBreak: "break-word",
          }}>
            {company?.name}
          </Box>
        </Box>

        {/* Location — centered */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "center", flexWrap: "wrap" }}>
          <Location countryCode={company?.address?.countryId} city={company?.address?.city} textClassName="text-12-medium" flagSize="small" />
          {company?.address?.distance != null && (
            <Box sx={{
              display: "inline-flex", alignItems: "center", padding: "1px 8px", borderRadius: "10px",
              fontSize: "11px", fontWeight: 600, fontFamily: dt.fontMono,
              background: emerald.bg, color: emerald.color, border: `1px solid ${emerald.border}`, whiteSpace: "nowrap",
            }}>
              {Math.round(company.address.distance)} km
            </Box>
          )}
        </Box>

        {/* Divider */}
        {hasTier2 && (
          <Box sx={{ height: "1px", background: dt.cardBorder, mt: "8px", mb: "8px" }} />
        )}

        {/* TIER 2: Role labels + machine count — centered & stacked for phone */}
        {hasTier2 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "3px", alignItems: "center" }}>
            {hasRoleLabels && renderFullRoleLabels()}
            {hasMachineCount && (
              <Box sx={{ display: "flex", alignItems: "baseline", gap: "3px" }}>
                <Box sx={{ fontSize: "13px", fontWeight: 600, fontFamily: dt.fontMono, color: dt.rolePurple }}>
                  {machineCount}
                </Box>
                <Box sx={{ fontSize: "11px", fontWeight: 400, fontFamily: dt.font, color: dt.textMuted }}>
                  {machineCountLabel}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {renderGroupMenu()}
      </Box>
    );
  }

  // =====================================================================
  //  DESKTOP / TABLET LAYOUT
  // =====================================================================

  return (
    <Box
      onClick={handleClick}
      sx={{
        background: card.bg,
        border: card.border,
        borderRadius: card.radius,
        padding: "20px",
        boxShadow: card.shadow,
        position: "relative",
        fontFamily: dt.font,
        display: "flex",
        flexDirection: "column",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        cursor: "pointer",
        "&:hover": { transform: "translateY(-2px)", boxShadow: card.shadowHover },
        animation: `cardReveal 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${animDelay}s both`,
      }}
    >
      {/* Partner badge + bookmark — absolute top-right */}
      {renderTopRight()}

      {/* 2-column flex: image left, content right — SAME as RedesignedMachineCard */}
      <Box sx={{ display: "flex", gap: "20px", flex: 1 }}>
        {/* Logo area (left) */}
        <Box sx={{ flexShrink: 0 }}>
          {renderLogo(card.imageSizeLg)}
        </Box>

        {/* Content area (right) */}
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          {/* TIER 1: Identity Block */}
          {/* Line 1 — Company name */}
          <Box sx={{
            fontSize: "16px", fontWeight: 600, fontFamily: dt.font, color: text.primary,
            lineHeight: 1.3, paddingRight: "60px",
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
            overflow: "hidden", wordBreak: "break-word",
          }}>
            {company?.name}
          </Box>

          {/* Line 2 — Location + interpunct + distance pill */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "4px", flexWrap: "wrap" }}>
            <Location countryCode={company?.address?.countryId} city={company?.address?.city} textClassName="text-12-medium" flagSize="small" />
            {company?.address?.distance != null && (
              <>
                <Box sx={{ color: dt.textMuted, fontSize: "12px", lineHeight: 1 }}>&middot;</Box>
                <Box sx={{
                  display: "inline-flex", alignItems: "center", padding: "1px 8px", borderRadius: "10px",
                  fontSize: "11px", fontWeight: 600, fontFamily: dt.fontMono,
                  background: emerald.bg, color: emerald.color, border: `1px solid ${emerald.border}`, whiteSpace: "nowrap",
                }}>
                  {Math.round(company.address.distance)} km
                </Box>
              </>
            )}
          </Box>

          {/* DIVIDER between Tier 1 and Tier 2 */}
          {hasTier2 && (
            <Box sx={{ height: "1px", background: dt.cardBorder, mt: "8px", mb: "8px" }} />
          )}

          {/* TIER 2: Role labels (left) + Machine count (right) — single line */}
          {hasTier2 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              {hasRoleLabels ? renderFullRoleLabels() : <Box />}
              {hasMachineCount && (
                <Box sx={{ display: "flex", alignItems: "baseline", gap: "3px", flexShrink: 0 }}>
                  <Box sx={{ fontSize: "13px", fontWeight: 600, fontFamily: dt.fontMono, color: dt.rolePurple }}>
                    {machineCount}
                  </Box>
                  <Box sx={{ fontSize: "11px", fontWeight: 400, fontFamily: dt.font, color: dt.textMuted }}>
                    {machineCountLabel}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {renderGroupMenu()}
    </Box>
  );
};

export default CompanyCard;
